<script setup lang="ts">
/**
 * UI-kit component
 * Category: Inputs
 * Name: Textarea
 * FIGMA: https://www.figma.com/design/CFyOwQUjj7uVRQTc03muvG/CS-UI-kit?node-id=15072-32172&m=dev
 */
import type { AppTextareaProps, AppTextareaEvent } from '../types/interfaces'

import { AppText } from '@shared/ui/typography'

import { useAppTextarea } from '../composables/use-app-textarea.hook'

defineOptions({
  name: 'AppTextarea',
})

const props = withDefaults(defineProps<AppTextareaProps>(), {
  id: null,
  name: '',
  value: '',
  label: '',
  className: '',
  inputClass: '',
  placeholder: '',
  optionalLabel: '',
  hint: '',
  errorText: '',
  disabled: false,
  required: false,
  readonly: false,
  mode: 'normal',
  maxLength: undefined,
})

const emit = defineEmits<{
  change: [AppTextareaEvent]
  input: [AppTextareaEvent]
  focus: [AppTextareaEvent]
  blur: [AppTextareaEvent]
}>()

const {
  refTextarea,
  refValue,
  refIsError,
  refIsSuccess,
  handleChange,
  handleInput,
  handleFocus,
  handleBlur,
} = useAppTextarea({
  props,
  onChange: (event: AppTextareaEvent) => emit('change', event),
  onInput: (event: AppTextareaEvent) => emit('input', event),
  onFocus: (event: AppTextareaEvent) => emit('focus', event),
  onBlur: (event: AppTextareaEvent) => emit('blur', event),
})
</script>

<template>
  <div :class="['app-textarea', className]">
    <div
      v-if="label || optionalLabel"
      class="app-textarea__label"
    >
      <AppText
        v-if="label"
        name="body2"
        tag="label"
        :for="id"
      >
        {{ label }} <span
          v-if="required"
          class="app-textarea__required"
        >*</span>
      </AppText>
      <AppText
        v-if="optionalLabel"
        name="body2"
        class="optional-label"
      >
        {{ optionalLabel }}
      </AppText>
    </div>
    <textarea
      :id="id ?? 'textArea'"
      ref="refTextarea"
      v-model="refValue"
      :class="['app-textarea__field', inputClass, { 'is-error': refIsError, 'is-success': refIsSuccess, readonly }]"
      :placeholder="placeholder"
      :maxlength="maxLength"
      :readonly="readonly"
      :disabled="disabled"
      :name="name"
      @change="handleChange"
      @input="handleInput"
      @focus="handleFocus"
      @blur="handleBlur"
      @wheel="$event.stopPropagation()"
    />
    <AppText
      v-if="refIsError && errorText"
      name="body2"
      color="red-600"
      class="app-textarea__error"
    >
      {{ errorText }}
    </AppText>
    <AppText
      v-else-if="hint"
      name="body2"
      color="slate-500"
      class="app-textarea__hint"
    >
      {{ hint }}
    </AppText>
  </div>
</template>

<style lang="scss" scoped src="../styles/_app-textarea.scss" />
